import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Light is the default for everyone. Only a DELIBERATE toggle is remembered.
  //
  // The old code read a key it also wrote on every load, so the previous dark default had
  // already been persisted for anyone who had ever opened the portal — changing the default
  // alone would have left all of them on dark, having never chosen it. Reading a separate
  // key that is written only by toggleTheme distinguishes "this person picked dark" from
  // "the app picked dark for them once".
  const [theme, setTheme] = useState(() => localStorage.getItem('theme-choice') || 'light');

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    root.setAttribute('data-theme', theme);
    // Deliberately NOT persisted here — see above. Applying a theme is not choosing one.
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => {
    const next = prev === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme-choice', next);
    return next;
  });
  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};
